/**
 * Thao tác quản trị được phép theo trạng thái tenant — bảng ADR-0025 mục 1.
 *
 * Bản Java là chốt chặn thật: `Tenant#isApprovable`, `isEditable`,
 * `isSlugEditable`, `isSuspendable`, `isResumable`, `resume` của tenant-service.
 * Giao diện chỉ dùng bản này để **ẩn** hành động không làm được (ADR-UI-0005) và
 * để dữ liệu mẫu từ chối giống backend. **Sửa ở đây thì sửa cả bên Java**, và
 * ngược lại.
 *
 * | Trạng thái   | Duyệt | Sửa hồ sơ | Sửa slug | Tạm ngưng | Khôi phục |
 * | ------------ | ----- | --------- | -------- | --------- | --------- |
 * | DRAFT        | Có    | Có        | Có       |           |           |
 * | PROVISIONING |       |           |          |           |           |
 * | TRIAL        |       | Có        |          | Có        |           |
 * | ACTIVE       |       | Có        |          | Có        |           |
 * | GRACE_PERIOD |       | Có        |          | Có        |           |
 * | SUSPENDED    |       | Có        |          |           | Có        |
 * | ARCHIVED     |       |           |          |           |           |
 * | DELETED      |       |           |          |           |           |
 */
import type { DeploymentMode, TenantDetail, TenantStatus } from '../types.ts';

const SUA_DUOC: ReadonlySet<TenantStatus> = new Set<TenantStatus>([
  'DRAFT',
  'TRIAL',
  'ACTIVE',
  'GRACE_PERIOD',
  'SUSPENDED',
]);

const TAM_NGUNG_DUOC: ReadonlySet<TenantStatus> = new Set<TenantStatus>([
  'TRIAL',
  'ACTIVE',
  'GRACE_PERIOD',
]);

/**
 * Duyệt hồ sơ (UC-02): chỉ tenant DRAFT, kể cả tenant đã triển khai hỏng và
 * được workflow trả về DRAFT — duyệt lại là cách chạy lại onboarding.
 */
export const duyetDuoc = (trangThai: TenantStatus): boolean => trangThai === 'DRAFT';

/** PROVISIONING, ARCHIVED, DELETED không sửa được hồ sơ, gói, tầng. */
export const suaDuoc = (trangThai: TenantStatus): boolean => SUA_DUOC.has(trangThai);

/** Slug khoá từ lúc duyệt (UC-02): đã nằm trong tên database và đường dẫn group. */
export const suaSlugDuoc = (trangThai: TenantStatus): boolean => trangThai === 'DRAFT';

export const tamNgungDuoc = (trangThai: TenantStatus): boolean => TAM_NGUNG_DUOC.has(trangThai);

export const khoiPhucDuoc = (trangThai: TenantStatus): boolean => trangThai === 'SUSPENDED';

/**
 * Dòng có hiện ở danh sách tenant không. Tenant đã xoá mềm chỉ hiện khi lọc đúng "Đã xoá";
 * lọc trạng thái khác thì chỉ hiện trạng thái đó.
 */
export const hienTrongDanhSach = (trangThai: TenantStatus, loc: TenantStatus | undefined): boolean =>
  loc ? trangThai === loc : trangThai !== 'DELETED';

/**
 * Trạng thái sau khi khôi phục (ADR-0025 mục 2, ADR-0026 mục 3).
 *
 * SAAS về ACTIVE chỉ khi có thuê bao ACTIVE, ngược lại về TRIAL — khôi phục
 * thẳng về ACTIVE là cho tenant dùng thử tính năng trả phí không qua thanh
 * toán. ON_PREMISE không có TRIAL nên luôn về ACTIVE. Trước GĐ4 chưa có thuê
 * bao nào, nên tenant SAAS luôn về TRIAL.
 */
export function trangThaiSauKhoiPhuc(
  cachTrienKhai: DeploymentMode,
  coThueBaoActive: boolean,
): TenantStatus {
  return coThueBaoActive || cachTrienKhai === 'ON_PREMISE' ? 'ACTIVE' : 'TRIAL';
}

/**
 * Tab "Người dùng" (ADR-0101): chỉ tenant đã có DB lõi và tài khoản Keycloak. DRAFT, PROVISIONING chưa có (hay chưa
 * xong) người dùng; DELETED đã xoá cả hai.
 */
export const coNguoiDung = (trangThai: TenantStatus): boolean =>
  trangThai !== 'DRAFT' && trangThai !== 'PROVISIONING' && trangThai !== 'DELETED';

/** Đổi email nhận thư của người trong tenant: tenant còn hoạt động hoặc đang tạm ngưng, không phải đã lưu trữ. */
export const doiEmailNguoiDungDuoc = (trangThai: TenantStatus): boolean =>
  coNguoiDung(trangThai) && trangThai !== 'ARCHIVED';

/** Workflow onboarding còn chạy: màn chi tiết hỏi lại tiến độ, mọi thao tác bị khoá. */
export const dangTrienKhai = (trangThai: TenantStatus): boolean => trangThai === 'PROVISIONING';

/** Hồ sơ còn thiếu gì để onboarding chạy tới cuối (ADR-0028 mục 4). */
export type CanTroDuyet = 'THIEU_DOMAIN_CHINH' | 'THIEU_LIEN_HE_CHINH' | 'EMAIL_NGOAI_DOMAIN_CHINH';

/**
 * Hồ sơ đủ để duyệt: tenant SAAS có domain chính, có người liên hệ chính, email thuộc domain đó. Tổ chức dùng email
 * cá nhân (`chi_email_domain_xac_minh === false`, ADR-0099) chỉ cần người liên hệ chính.
 */
type HoSoDuyet = Pick<TenantDetail, 'deployment_mode' | 'domains' | 'admin_contacts'> &
  Partial<Pick<TenantDetail, 'chi_email_domain_xac_minh'>>;

/** Tổ chức dùng email cá nhân (ADR-0099): thiếu trường là bật chính sách, như Gateway coi thiếu là chiều chặt. */
export const dungEmailCaNhan = (tenant: Partial<Pick<TenantDetail, 'chi_email_domain_xac_minh'>>): boolean =>
  tenant.chi_email_domain_xac_minh === false;

const domainCuaEmail = (email: string): string => email.slice(email.lastIndexOf('@') + 1).toLowerCase();

/**
 * Những gì chặn lần duyệt này, theo `TenantService#requireOnboardable` của
 * tenant-service — bước 5c chờ domain chính xác minh, bước 6 tạo Tenant Admin
 * đầu tiên từ người liên hệ chính, và bước 6 chỉ nhận email thuộc domain đã
 * xác minh (ADR-0009 mục 2, 5).
 *
 * Backend ném lỗi **đầu tiên** gặp phải; ở đây trả hết để hộp xác nhận nói một
 * lần người vận hành phải bổ sung những gì, thay vì để họ duyệt ba lần mới đủ.
 * Tenant ON_PREMISE không onboarding nên không có cản trở nào (mục 14.1). Tổ chức dùng email cá nhân (ADR-0099 mục 4)
 * không cần domain: bước 5c không chờ, Tenant Admin đầu tiên xác minh email qua liên kết mời.
 */
export function canTroDuyet(tenant: HoSoDuyet): CanTroDuyet[] {
  if (tenant.deployment_mode === 'ON_PREMISE') return [];
  if (dungEmailCaNhan(tenant)) {
    return tenant.admin_contacts.some((c) => c.is_primary) ? [] : ['THIEU_LIEN_HE_CHINH'];
  }

  const domainChinh = tenant.domains.find((d) => d.is_primary);
  const lienHeChinh = tenant.admin_contacts.find((c) => c.is_primary);

  const canTro: CanTroDuyet[] = [];
  if (!domainChinh) canTro.push('THIEU_DOMAIN_CHINH');
  if (!lienHeChinh) canTro.push('THIEU_LIEN_HE_CHINH');
  if (domainChinh && lienHeChinh && domainCuaEmail(lienHeChinh.email) !== domainChinh.domain.toLowerCase()) {
    canTro.push('EMAIL_NGOAI_DOMAIN_CHINH');
  }
  return canTro;
}

/**
 * Domain chính chưa xác minh: hồ sơ vẫn duyệt được, nhưng workflow dừng ở bước
 * 5c chờ xác minh, tối đa 14 ngày (ADR-0009). Người bấm duyệt cần biết trước,
 * nếu không họ sẽ ngồi nhìn tiến độ đứng yên mà tưởng hỏng.
 */
export function choXacMinhDomain(tenant: HoSoDuyet): boolean {
  if (tenant.deployment_mode === 'ON_PREMISE' || dungEmailCaNhan(tenant)) return false;
  const domainChinh = tenant.domains.find((d) => d.is_primary);
  return Boolean(domainChinh && !domainChinh.verified_at);
}

/**
 * Kiểm lại DNS và xác minh thủ công domain được ở mọi trạng thái **trừ**
 * ARCHIVED, DELETED (`409 TENANT_STATUS_CONFLICT`, ADR-0054). DRAFT cũng được:
 * xác minh trước khi duyệt thì onboarding không phải dừng ở bước 5c.
 */
export const xacMinhDomainDuoc = (trangThai: TenantStatus): boolean =>
  trangThai !== 'ARCHIVED' && trangThai !== 'DELETED';

/** Trần độ dài lý do xác minh thủ công, như `maxLength` của `TenantDomainApproval`. */
export const LY_DO_TOI_DA = 1000;

/** Lý do xác minh thủ công còn thiếu gì: trống (kể cả chỉ khoảng trắng) hay quá dài. */
export function kiemLyDo(lyDo: string): 'TRONG' | 'DAI' | null {
  const gon = lyDo.trim();
  if (gon.length === 0) return 'TRONG';
  if (gon.length > LY_DO_TOI_DA) return 'DAI';
  return null;
}

type HoSoGoDomain = Pick<TenantDetail, 'status' | 'domains'> & Partial<Pick<TenantDetail, 'chi_email_domain_xac_minh'>>;

/**
 * Gỡ được domain này không (GG-FX-39), theo `DomainRemovalService` của tenant-service — **sửa ở đây thì sửa cả bên
 * Java**. Tenant bật "Chỉ email thuộc domain đã xác minh" phải giữ ít nhất một domain đã xác minh: không gỡ domain
 * duy nhất, không gỡ domain đã xác minh cuối cùng. Tắt chính sách (ADR-0099) thì gỡ được mọi domain. Tenant đang
 * triển khai thì backend từ chối; tenant đã lưu trữ hay đã xoá thì tab Domain chỉ đọc nên giao diện cũng không mời gỡ.
 *
 * Luật "còn người dùng đăng nhập bằng domain này" chỉ backend biết; khi đó hộp xác nhận hiện câu từ chối của API.
 */
export function goDomainDuoc(tenant: HoSoGoDomain, domain: string): boolean {
  if (!xacMinhDomainDuoc(tenant.status) || dangTrienKhai(tenant.status)) return false;
  const dong = tenant.domains.find((d) => d.domain === domain);
  if (!dong) return false;
  if (dungEmailCaNhan(tenant)) return true;
  const conLai = tenant.domains.filter((d) => d.domain !== domain);
  if (conLai.length === 0) return false;
  return !dong.verified_at || conLai.some((d) => d.verified_at);
}

/**
 * Domain lên làm domain chính khi gỡ `domain` đang là chính: domain đã xác minh cũ nhất còn lại, không có thì domain cũ
 * nhất còn lại; `null` khi không còn domain nào hoặc domain gỡ không phải domain chính. Như `DomainRemovalService`.
 */
export function domainChinhSauKhiGo(tenant: Pick<TenantDetail, 'domains'>, domain: string): string | null {
  const dong = tenant.domains.find((d) => d.domain === domain);
  if (!dong?.is_primary) return null;
  const conLai = tenant.domains
    .filter((d) => d.domain !== domain)
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.domain.localeCompare(b.domain));
  return (conLai.find((d) => d.verified_at) ?? conLai[0])?.domain ?? null;
}
